import { EmptyState, FlexBox, Skeleton } from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import { User } from 'lib-shared-types-api';
import { useEffect } from 'react';
import { Waypoint } from 'react-waypoint';

import { auditLogsApi, UserActivityType } from 'apis/auditLogs';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint';

import { isDateRangeValid } from './activityLog';
import { ComponentByUserLogType } from './userActivityTypes';

export type UserActivityLogFiltersValue = {
  fromTs?: string;
  toTs?: string;
  eventTypes?: UserActivityType[];
};

type UserActivityLogFeedProps = {
  /** The contact/user whose activities are displayed. */
  readonly user: User;
  readonly filters: UserActivityLogFiltersValue;
  readonly sortDirection?: 'asc' | 'desc';
  readonly onClearFilters: () => void;
};

/**
 * Fetches and renders a user's activity entries, including the loading, empty,
 * error and invalid date range states.
 */
export function UserActivityLogFeed({
  user,
  filters,
  sortDirection = 'desc',
  onClearFilters,
}: UserActivityLogFeedProps): JSX.Element {
  const {
    items: entries,
    changeArg,
    asyncStatus,
    hasNextPage,
    nextPage,
  } = useLazyPaginatedEndpoint({
    endpoint: auditLogsApi.endpoints.readUserAuditLogs,
    itemsSelector: (response) => response.data.logs,
    infinite: true,
  });

  const hasActiveFilters = Boolean(
    filters.fromTs || filters.toTs || filters.eventTypes?.length,
  );
  const isDateRangeInvalid = !isDateRangeValid(filters.fromTs, filters.toTs);

  const clearFiltersButtonProps = hasActiveFilters
    ? {
        children: 'Clear filters',
        variant: 'secondary' as const,
        onClick: onClearFilters,
      }
    : undefined;

  useEffect(() => {
    if (isDateRangeValid(filters.fromTs, filters.toTs)) {
      changeArg({
        resourceId: user.id,
        limit: 100,
        sort: { key: 'timestamp', direction: sortDirection },
        fromTs: filters.fromTs,
        toTs: filters.toTs,
        eventTypes: filters.eventTypes,
      });
    }
  }, [changeArg, user.id, filters, sortDirection]);

  if (isDateRangeInvalid) {
    return (
      <EmptyState
        icon="calendar-times"
        title="Invalid date range"
        description="The start date is after the end date. Adjust the range to see the activities."
        buttonProps={clearFiltersButtonProps}
      />
    );
  }

  return (
    <>
      {asyncStatus === 'rejected' && (
        <EmptyState
          icon="exclamation-triangle-filled"
          title="Failed to retrieve customer activities"
          description="Something went wrong while loading the activity feed."
        />
      )}

      {entries.map((entry) => (
        <ComponentByUserLogType
          key={entry.event_id}
          entry={entry}
          userType={user.type}
        />
      ))}

      {asyncStatus === 'fulfilled' && !entries.length && (
        <EmptyState
          icon="file-text-search"
          title="No activities found"
          description={
            hasActiveFilters
              ? 'No activity matches the selected filters. Try clearing them to see more.'
              : 'There are no activities to display for this contact.'
          }
          buttonProps={clearFiltersButtonProps}
        />
      )}

      {(asyncStatus === 'pending' || asyncStatus === 'uninitialized') &&
        Array.from({ length: 6 }).map((_, index) => (
          <FlexBox key={index} alignItems="center" gap="300" padding="200 0">
            <Skeleton
              width="24px"
              height="24px"
              borderRadius={sizes['border-radius']['100']}
            />
            <FlexBox flexDirection="column" gap="100" flex="1">
              <Skeleton width="30%" height="18px" />
              <Skeleton width="50%" height="16px" />
            </FlexBox>
            <Skeleton width="64px" height="16px" />
          </FlexBox>
        ))}

      {asyncStatus === 'fulfilled' && hasNextPage && (
        <Waypoint onEnter={nextPage} />
      )}
    </>
  );
}
